/**
 * Curated genitive forms for canonical Finnish role names shown in salary-page titles/
 * descriptions ("Sairaanhoitajan palkka", not the nominative "Sairaanhoitaja palkka"; the
 * genitive is the form people actually search). Finnish genitive formation depends on consonant
 * gradation and stem type, so beyond the one regular class handled by the fallback below, this is
 * a lookup, not a suffix rule.
 */
const FINNISH_ROLE_GENITIVE: Record<string, string> = {
  Sairaanhoitaja: 'Sairaanhoitajan',
  Lähihoitaja: 'Lähihoitajan',
  // Role-hub display names (ROLE_HUB_CONFIG) whose ending is not the regular -ja/-jä class.
  // -kkö/-kko: kk > k gradation. -ri/-tti/-kko: plain -n with gradation where it applies.
  // Multi-word names: the adjective agrees too ("Teknisen myyjän").
  Kokki: 'Kokin',
  Projektipäällikkö: 'Projektipäällikön',
  Tuotepäällikkö: 'Tuotepäällikön',
  Myyntipäällikkö: 'Myyntipäällikön',
  Asiakkuuspäällikkö: 'Asiakkuuspäällikön',
  Vuoropäällikkö: 'Vuoropäällikön',
  Markkinointipäällikkö: 'Markkinointipäällikön',
  Tuotantopäällikkö: 'Tuotantopäällikön',
  Kirvesmies: 'Kirvesmiehen',
  'Kaivinkoneen perämies': 'Kaivinkoneen perämiehen',
  Rakennusapulainen: 'Rakennusapulaisen',
  'Henkilökohtainen avustaja': 'Henkilökohtaisen avustajan',
  'Tekninen myyjä': 'Teknisen myyjän',
  Lääkäri: 'Lääkärin',
  Yleislääkäri: 'Yleislääkärin',
  Hammaslääkäri: 'Hammaslääkärin',
  Työterveyslääkäri: 'Työterveyslääkärin',
  Maalari: 'Maalarin',
  Teollisuusmaalari: 'Teollisuusmaalarin',
  Sähköinsinööri: 'Sähköinsinöörin',
  'Backend-insinööri': 'Backend-insinöörin',
  'Myynti-insinööri': 'Myynti-insinöörin',
  Huoltoteknikko: 'Huoltoteknikon',
  Automekaanikko: 'Automekaanikon',
  Mekaanikko: 'Mekaanikon',
  Analyytikko: 'Analyytikon',
  Ratkaisukonsultti: 'Ratkaisukonsultin',
  Puheterapeutti: 'Puheterapeutin',
};

/**
 * Best-effort Finnish genitive singular for a role display name. Agent nouns ending in -ja/-jä
 * (Finnish's standard "-er" suffix: hoitaja, kuljettaja, myyjä, opettaja, ...) are the single
 * largest class of Finnish job titles and take genitive by adding a plain -n with no consonant
 * gradation, because the letter before that final vowel is never one of the gradating consonants
 * (k/p/t), so this rule is safe to apply generally. Every other ending (-kkö, -nen, -mies, -i, ...)
 * needs real gradation or irregular handling that a suffix rule gets wrong more often than right,
 * so those fall back to the curated map above, and to the nominative (unchanged) form when not
 * yet curated: wrong grammar is worse than missing grammar.
 */
export function getFinnishRoleGenitive(title: string): string {
  const trimmed = title.trim();
  if (FINNISH_ROLE_GENITIVE[trimmed]) return FINNISH_ROLE_GENITIVE[trimmed];
  if (/[jJ][aäAÄ]$/.test(trimmed)) return `${trimmed}n`;
  return trimmed;
}
